import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Alert, Box, CircularProgress } from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { getInvitationBySlug } from "../../utils/invitationsApi";
import InvitationRSVPForm from "./InvitationRSVPForm";
import {
  DEFAULT_FONT_SETTINGS,
  FONT_SETTING_KEYS,
  buildGoogleFontsUrl,
  isGoogleFontFamily,
  normalizeFontDescriptor,
  sanitizeFontSettings,
} from "./invitationFonts";
import {
  formatMkFullDate,
  formatMkPrimaryDate,
  formatMkTime,
} from "./invitationDateUtils";
import HeroSection from "./sections/HeroSection";
import IntroSection from "./sections/IntroSection";
import CountdownSection from "./sections/CountdownSection";
import RSVPSection from "./sections/RSVPSection";
import ContactSection from "./sections/ContactSection";
import LocationSection from "./sections/LocationSection";
import FooterSection from "./sections/FooterSection";
import "./FullWidthInvitation.css";

const PUBLIC_GOOGLE_FONT_LINK_ID = "invitation-public-google-fonts-fw";

const extractTemplateFontDefaults = (fontSettings) => {
  if (!fontSettings || typeof fontSettings !== "object") {
    return {};
  }
  return Object.entries(fontSettings).reduce((acc, [key, value]) => {
    const asArray = Array.isArray(value) ? value : [value];
    const normalizedOptions = asArray
      .map((entry) => normalizeFontDescriptor(entry))
      .filter(Boolean);
    if (normalizedOptions.length) {
      acc[key] = normalizedOptions[0];
    }
    return acc;
  }, {});
};

const FullWidthInvitationPage = () => {
  const { slug } = useParams();
  const { authPayload } = useAuth();

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [countdownParts, setCountdownParts] = useState(null);
  const [countdownFinished, setCountdownFinished] = useState(false);

  const isOwner = Boolean(authPayload?.user_id);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getInvitationBySlug(slug);
        if (!cancelled) {
          setInvitation(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError("Не можеме да ја вчитаме поканата.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const rawTemplateConfig = invitation?.template?.json_structure;

  const templateConfig = useMemo(() => {
    if (!rawTemplateConfig) return {};
    if (typeof rawTemplateConfig === "string") {
      try {
        return JSON.parse(rawTemplateConfig);
      } catch {
        return {};
      }
    }
    return rawTemplateConfig;
  }, [rawTemplateConfig]);

  const templateFontDefaults = useMemo(
    () => extractTemplateFontDefaults(templateConfig?.font_settings),
    [templateConfig]
  );

  const invitationFontSettings = useMemo(
    () => sanitizeFontSettings(invitation?.font_settings),
    [invitation?.font_settings]
  );

  const combinedFontSettings = useMemo(() => {
    const map = {};
    FONT_SETTING_KEYS.forEach((key) => {
      map[key] =
        invitationFontSettings[key] ||
        templateFontDefaults[key] ||
        DEFAULT_FONT_SETTINGS[key] ||
        null;
    });
    return map;
  }, [invitationFontSettings, templateFontDefaults]);

  const fontStyleMap = useMemo(() => {
    const styles = {};
    FONT_SETTING_KEYS.forEach((key) => {
      const descriptor = combinedFontSettings[key];
      if (descriptor?.family) {
        styles[key] = {
          fontFamily: descriptor.family,
          ...(descriptor.weight ? { fontWeight: descriptor.weight } : {}),
          ...(descriptor.style ? { fontStyle: descriptor.style } : {}),
        };
      } else {
        styles[key] = {};
      }
    });
    return styles;
  }, [combinedFontSettings]);

  const googleFontsSelection = useMemo(
    () =>
      FONT_SETTING_KEYS.map((key) => combinedFontSettings[key]).filter(
        (descriptor) =>
          descriptor?.family && isGoogleFontFamily(descriptor.family)
      ),
    [combinedFontSettings]
  );

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }
    const href = buildGoogleFontsUrl(googleFontsSelection);
    const existingLink = document.getElementById(PUBLIC_GOOGLE_FONT_LINK_ID);
    if (!href) {
      if (existingLink) {
        existingLink.remove();
      }
      return undefined;
    }
    if (existingLink && existingLink.getAttribute("href") === href) {
      return undefined;
    }
    const link = document.createElement("link");
    link.id = PUBLIC_GOOGLE_FONT_LINK_ID;
    link.rel = "stylesheet";
    link.href = href;
    if (existingLink) {
      existingLink.remove();
    }
    document.head.appendChild(link);
    return () => {
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
    };
  }, [googleFontsSelection]);

  const theme = useMemo(() => {
    const palette = templateConfig?.palette || {};
    const colors = invitation?.theme_colors;
    const templatePrimary = palette.primary || "#2d2a26";

    return {
      primary: (colors && colors.primary) || templatePrimary,
      secondary:
        (colors && colors.secondary) ||
        templatePrimary ||
        "#b89b5e",
    };
  }, [invitation, templateConfig]);

  const eventDate = useMemo(() => {
    return invitation?.event_date
      ? new Date(invitation.event_date)
      : null;
  }, [invitation?.event_date]);

  const primaryDateText = useMemo(() => {
    return formatMkPrimaryDate(eventDate);
  }, [eventDate]);

  const fullDateText = useMemo(() => {
    return formatMkFullDate(eventDate);
  }, [eventDate]);

  const timeText = useMemo(() => {
    return formatMkTime(eventDate);
  }, [eventDate]);

  const mapEmbedUrl = invitation?.venue_name
    ? `https://www.google.com/maps?q=${encodeURIComponent(
        invitation.venue_name
      )}&output=embed`
    : null;

  useEffect(() => {
    if (!invitation?.event_date) {
      setCountdownParts(null);
      setCountdownFinished(false);
      return;
    }

    const targetTime = new Date(invitation.event_date).getTime();
    let timerId;

    const updateCountdown = () => {
      const diff = targetTime - Date.now();

      if (diff <= 0) {
        setCountdownFinished(true);
        setCountdownParts({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        if (timerId) {
          clearInterval(timerId);
        }
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      setCountdownFinished(false);
      setCountdownParts({ days, hours, minutes, seconds });
    };

    updateCountdown();
    timerId = setInterval(updateCountdown, 1000);

    return () => {
      clearInterval(timerId);
    };
  }, [invitation?.event_date]);

  if (loading) {
    return (
      <Box className="fw-invitation-loading">
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="fw-invitation-loading">
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!invitation) {
    return null;
  }

  return (
    <div
      className="fw-invitation-page"
      style={{
        "--fw-primary": theme.primary,
        "--fw-secondary": theme.secondary,
      }}
    >
      <HeroSection
        invitation={invitation}
        primaryDateText={primaryDateText}
        fontStyleMap={fontStyleMap}
      />

      <IntroSection invitation={invitation} fontStyleMap={fontStyleMap} />

      <CountdownSection
        countdownParts={countdownParts}
        finished={countdownFinished}
        fullDateText={fullDateText}
        timeText={timeText}
        fontStyleMap={fontStyleMap}
      />

      <RSVPSection
        invitation={invitation}
        fontStyleMap={fontStyleMap}
        slug={slug}
        isOwner={isOwner}
        theme={theme}
        InvitationRSVPForm={InvitationRSVPForm}
      />

      <ContactSection invitation={invitation} fontStyleMap={fontStyleMap} />

      <LocationSection
        invitation={invitation}
        mapEmbedUrl={mapEmbedUrl}
        fontStyleMap={fontStyleMap}
      />

      <FooterSection />
    </div>
  );
};

export default FullWidthInvitationPage;
